import React, { useContext, useEffect, useRef, useState } from "react";
import DayPickerInput from "react-day-picker/DayPickerInput";
import { ReactComponent as CalendarIcon } from "assets/icons/calendar.svg";
import { ReactComponent as ChevronIcon } from "assets/icons/chevron.svg";
import { Input } from "..";
import { Navbar } from "./Navbar";
import "../../../../css/rangeDatePicker.component.css";
import { DayModifiers, Modifiers } from "react-day-picker";
import { format } from "date-fns";
import { ReactComponent as TrashRedIcon } from "../../../../assets/icons/trash-red.svg";
import { AccessibilityContext } from "views/AccessibilityContext";
import { WEEKDAYS_SHORT } from "modules/common/constants";

interface Props {
    onClick: (
        day: Date,
        modifiers: DayModifiers,
        e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => void;
    selectedDays: {
        from: Date | undefined;
        to: Date | undefined;
    };
    modifiers: Partial<Modifiers> | undefined;
    updateRange: (from: Date | undefined, to: Date | undefined) => void;
    disableFutureDays?: boolean;
}
const DateInput = React.forwardRef((props: any, ref) => {
    return (
        <Input
            {...props}
            name="DatePickerInput"
            className="day-picker-input date-picker-input"
            type="text"
            size="small"
            leftIcon={<CalendarIcon />}
            rightIcon={<ChevronIcon />}
        />
    );
});

const firstDayOfWeek = {
    hr: 1,
};

export const RangeDatePicker: React.FC<Props> = ({
    onClick,
    selectedDays,
    modifiers,
    updateRange,
    disableFutureDays,
}) => {
    const [showOverlay, setShowOverlay] = useState(false);
    const [selectedControl, setSelectedControl] = useState(0);
    const dateRangeContainerRef = useRef<HTMLDivElement>(null);
    const dateRangeRef = useRef<DayPickerInput>(null);
    const today = new Date();
    const { lowVision } = useContext(AccessibilityContext);

    const handleClick = (e: any) => {
        const dayPickerNode = dateRangeContainerRef.current;

        if (dayPickerNode && !dayPickerNode.contains(e.target)) {
            dateRangeRef.current?.hideDayPicker();
            return;
        }

        setShowOverlay(true);
    };

    const handleClearClick = () => {
        updateRange(undefined, undefined);
        dateRangeRef.current?.hideDayPicker();
    };

    const setToday = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        const { id } = e.currentTarget;

        setSelectedControl(parseInt(id));
        updateRange(today, today);
    };

    const formatValue = (date: Date) => {
        return format(date, "dd.MM.yy");
    };

    const Overlay = ({ classNames, selectedDay, children, ...props }: any) => {
        return (
            <div
                className={
                    lowVision ? "low-vision DayPickerInput-Overlay" : classNames.overlayWrapper
                }
                {...props}
            >
                <div className={classNames.overlay}>
                    {children}
                    <div
                        className="DayPicker-Footer"
                        style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}
                    >
                        <button type="button" className="DayPicker-TodayButton" onClick={setToday}>
                            Danas
                        </button>

                        <button
                            type="button"
                            className="DayPicker-TrashButton"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                            onClick={handleClearClick}
                            aria-label="Clear selection"
                            title="Clear selection"
                        >
                            <TrashRedIcon style={{ width: 15, height: 15 }} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClick);
        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, []);

    return (
        <div ref={dateRangeContainerRef}>
            <DayPickerInput
                overlayComponent={Overlay}
                value={
                    selectedDays.from && selectedDays.to
                        ? `${formatValue(selectedDays.from)} - ${formatValue(selectedDays.to)}`
                        : selectedDays.from
                        ? formatValue(selectedDays.from)
                        : undefined
                }
                format="dd.MM.yyyy"
                formatDate={formatValue}
                component={DateInput}
                ref={dateRangeRef}
                inputProps={{ name: "DatePickerInput" }}
                hideOnDayClick={false}
                keepFocus={true}
                placeholder="Odaberite datum"
                onDayPickerShow={() => setShowOverlay(true)}
                onDayPickerHide={() => setShowOverlay(false)}
                showOverlay={showOverlay}
                dayPickerProps={{
                    navbarElement: <Navbar />,
                    className: "Range",
                    locale: "hr",
                    numberOfMonths: 1,
                    weekdaysShort: WEEKDAYS_SHORT,
                    firstDayOfWeek: firstDayOfWeek["hr"],
                    modifiers: modifiers,
                    onDayClick: onClick,
                    selectedDays: selectedDays.from && selectedDays.to 
                        ? { from: selectedDays.from, to: selectedDays.to }
                        : selectedDays.from 
                        ? selectedDays.from 
                        : undefined,
                    disabledDays: disableFutureDays ? { after: today } : undefined,
                }}
            />
        </div>
    );
};
